import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { inventoryService } from '../../services'

const formatAmount = (value) => {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const formatQuantity = (value) => {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return Number.isInteger(num) ? String(num) : num.toFixed(2)
}

function ProductInventoryLotsPanel({ sku, refreshKey = 0 }) {
  const [lots, setLots] = useState([])
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [locationFilter, setLocationFilter] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const requestIdRef = useRef(0)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    setPage(0)
  }, [locationFilter, debouncedSearch, sku])

  useEffect(() => {
    if (!sku) {
      setLots([])
      setLocations([])
      setTotal(0)
      return undefined
    }

    let cancelled = false
    const requestId = ++requestIdRef.current

    const loadLots = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await inventoryService.getInventoryPriceOptions(sku, {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch || undefined,
          location_code: locationFilter?.code || undefined,
        })
        if (cancelled || requestId !== requestIdRef.current) return
        setLots(response?.options || [])
        setLocations(response?.locations || [])
        setTotal(Number(response?.pagination?.total) || 0)
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return
        console.error('Error fetching product inventory lots:', err)
        setLots([])
        setLocations([])
        setTotal(0)
        setError(err.response?.data?.message || 'Failed to fetch inventory lots')
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    }

    loadLots()
    return () => {
      cancelled = true
    }
  }, [sku, refreshKey, page, rowsPerPage, debouncedSearch, locationFilter])

  const locationOptions = useMemo(() => {
    if (locations.length > 0) {
      return locations.map((loc) => ({
        code: loc.code,
        label: loc.label || (loc.name ? `${loc.code} — ${loc.name}` : loc.code),
      }))
    }
    return []
  }, [locations])

  const handleChangePage = useCallback((_event, newPage) => {
    setPage(newPage)
  }, [])

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10) || 10)
    setPage(0)
  }, [])

  if (!sku) {
    return <Alert severity="info">No Article Code available for this product.</Alert>
  }

  if (loading && lots.length === 0 && total === 0 && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (error && lots.length === 0) {
    return <Alert severity="error">{error}</Alert>
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
          alignItems: 'flex-start',
        }}
      >
        <TextField
          label="Search locations"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          size="small"
          placeholder="Filter by location code or name..."
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />
        <Autocomplete
          options={locationOptions}
          value={locationFilter}
          onChange={(_event, value) => setLocationFilter(value)}
          getOptionLabel={(option) => option?.label || option?.code || ''}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          renderInput={(params) => (
            <TextField {...params} label="Location" size="small" placeholder="All locations" />
          )}
          sx={{ minWidth: 240, flex: '1 1 240px' }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {total} location{total === 1 ? '' : 's'}
          {loading ? '…' : ''}
        </Typography>
      </Box>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && lots.length === 0 ? (
        <Alert severity="info">No inventory records found for this product.</Alert>
      ) : (
        <>
          <TableContainer sx={{ position: 'relative', opacity: loading ? 0.6 : 1 }}>
            {loading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : null}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Selling Price</TableCell>
                  <TableCell align="right">MRP</TableCell>
                  <TableCell align="right">Qty on Hand</TableCell>
                  <TableCell align="right">Reserved</TableCell>
                  <TableCell align="right">Available</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lots.map((lot) => (
                  <TableRow
                    key={
                      lot.inventory_id ||
                      `${lot.location_code}-${lot.unit_cost}-${lot.unit_price}`
                    }
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {lot.location_code || '—'}
                      </Typography>
                      {lot.location_name ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {lot.location_name}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{formatAmount(lot.unit_cost)}</TableCell>
                    <TableCell align="right">{formatAmount(lot.unit_price)}</TableCell>
                    <TableCell align="right">{formatQuantity(lot.quantity_on_hand)}</TableCell>
                    <TableCell align="right">{formatQuantity(lot.reserved_quantity)}</TableCell>
                    <TableCell align="right">
                      {formatQuantity(lot.computed_available_quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
    </Box>
  )
}

export default ProductInventoryLotsPanel
